import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SchoolSelector } from '@/components/SchoolSelector';
import {
  FileText, Download, Send, BarChart3, Calendar, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Activity, Building2, Heart, Mail as MailIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS_SV = ['Januari','Februari','Mars','April','Maj','Juni','Juli','Augusti','September','Oktober','November','December'];
const WEEKDAYS_SV = ['Mån','Tis','Ons','Tor','Fre','Lör','Sön'];

function KpiBox({ value, label, icon: Icon, color }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-3xl font-extrabold">{value}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
        </div>
        <Icon className="h-8 w-8 opacity-20" />
      </div>
    </div>
  );
}

function Heatmap({ data }) {
  if (!data || !data.length) return null;
  const max = Math.max(...data.flat(), 1);
  return (
    <div className="overflow-x-auto" data-testid="heatmap">
      <div className="grid gap-0.5" style={{ gridTemplateColumns: `40px repeat(24, 1fr)`, minWidth: 600 }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-center text-[9px] text-muted-foreground font-mono">{h}</div>
        ))}
        {WEEKDAYS_SV.map((day, d) => (
          <div key={`row-${d}`} className="contents">
            <div className="text-[10px] font-medium text-muted-foreground flex items-center">{day}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const val = data[d]?.[h] || 0;
              const intensity = val / max;
              let bg = 'bg-slate-100 dark:bg-slate-800';
              if (intensity > 0.7) bg = 'bg-red-500 text-white';
              else if (intensity > 0.3) bg = 'bg-amber-400 text-white';
              else if (intensity > 0) bg = 'bg-blue-200 dark:bg-blue-900';
              return (
                <div key={`${d}-${h}`} className={`${bg} rounded-sm text-center text-[8px] font-mono leading-5 h-5`} title={`${day} ${h}:00 — ${val} händelser`}>
                  {val > 0 ? val : ''}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2 text-[9px] text-muted-foreground">
        <span>Färre</span>
        <div className="h-2.5 w-4 rounded-sm bg-slate-100 dark:bg-slate-800" />
        <div className="h-2.5 w-4 rounded-sm bg-blue-200 dark:bg-blue-900" />
        <div className="h-2.5 w-4 rounded-sm bg-amber-400" />
        <div className="h-2.5 w-4 rounded-sm bg-red-500" />
        <span>Fler</span>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState('monthly');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('all');
  const [reportData, setReportData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [healthMonths, setHealthMonths] = useState(6);
  // Parent letter
  const [letterStart, setLetterStart] = useState('');
  const [letterEnd, setLetterEnd] = useState('');
  const [letterText, setLetterText] = useState('');

  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API}/api/schools`, { headers: h }).then(r => r.ok ? r.json() : []).then(list => {
      setSchools(list);
      if (Array.isArray(list) && list.length === 1) setSelectedSchool(list[0].id);
    });
    const now = new Date();
    const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setLetterStart(monthAgo.toISOString().slice(0, 10));
    setLetterEnd(now.toISOString().slice(0, 10));
  }, [token]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ year: reportYear, month: reportMonth });
    if (selectedSchool && selectedSchool !== 'all') params.set('school_id', selectedSchool);
    const r = await fetch(`${API}/api/reports/monthly?${params}`, { headers: h });
    if (r.ok) setReportData(await r.json());
    setLoading(false);
  }, [token, selectedSchool, reportYear, reportMonth]);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ months: healthMonths });
    if (selectedSchool && selectedSchool !== 'all') params.set('school_id', selectedSchool);
    const r = await fetch(`${API}/api/reports/student-health?${params}`, { headers: h });
    if (r.ok) setHealthData(await r.json());
    setLoading(false);
  }, [token, selectedSchool, healthMonths]);

  useEffect(() => { if (tab === 'monthly') loadReport(); }, [tab, loadReport]);
  useEffect(() => { if (tab === 'health') loadHealth(); }, [tab, loadHealth]);

  const downloadPdf = async () => {
    const params = new URLSearchParams({ year: reportYear, month: reportMonth });
    if (selectedSchool && selectedSchool !== 'all') params.set('school_id', selectedSchool);
    try {
      const r = await fetch(`${API}/api/reports/monthly/pdf?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `rapport-${reportYear}-${reportMonth}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error('Kunde inte ladda ner PDF'); }
  };

  const sendReport = async () => {
    if (!selectedSchool || selectedSchool === 'all') { toast.error('Välj en skola först'); return; }
    const params = new URLSearchParams({ school_id: selectedSchool, year: reportYear, month: reportMonth });
    const r = await fetch(`${API}/api/reports/monthly/send?${params}`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      if (d.ok) toast.success(`Rapport skickad till ${d.sent_to?.join(', ')}`);
      else toast.error(d.error || 'Kunde inte skicka rapport');
    }
  };

  const downloadParentLetter = async () => {
    if (!selectedSchool || selectedSchool === 'all') { toast.error('Välj en skola först'); return; }
    const params = new URLSearchParams({ school_id: selectedSchool, start_date: letterStart, end_date: letterEnd, custom_text: letterText });
    try {
      const r = await fetch(`${API}/api/reports/parent-letter/pdf?${params}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'foraldrabrev.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Föräldrabrev genererat');
    } catch { toast.error('Kunde inte generera föräldrabrev'); }
  };

  const schoolName = schools.find(s => s.id === selectedSchool)?.name || 'Alla skolor';

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            Rapporter
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 ml-[46px]">Månadsrapporter, föräldrabrev & elevhälsa</p>
        </div>
        <SchoolSelector schools={schools} value={selectedSchool} onChange={setSelectedSchool} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="monthly" className="gap-1.5"><FileText className="h-3.5 w-3.5" /> Månadsrapport</TabsTrigger>
          <TabsTrigger value="health" className="gap-1.5"><Heart className="h-3.5 w-3.5" /> Elevhälsa</TabsTrigger>
          <TabsTrigger value="parent" className="gap-1.5"><MailIcon className="h-3.5 w-3.5" /> Föräldrabrev</TabsTrigger>
        </TabsList>

        {/* ===== MONTHLY REPORT ===== */}
        <TabsContent value="monthly" className="space-y-4 mt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={String(reportMonth)} onValueChange={v => setReportMonth(parseInt(v))}>
              <SelectTrigger className="w-36"><Calendar className="h-3.5 w-3.5 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_SV.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" className="w-24" value={reportYear} onChange={e => setReportYear(parseInt(e.target.value) || 2026)} />
            <Button variant="outline" size="sm" onClick={loadReport} disabled={loading}>
              <Activity className="h-3.5 w-3.5 mr-1" /> Uppdatera
            </Button>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-report-pdf">
              <Download className="h-3.5 w-3.5 mr-1" /> Ladda ner PDF
            </Button>
            {selectedSchool && selectedSchool !== 'all' && (
              <Button size="sm" onClick={sendReport} className="bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="send-report-btn">
                <Send className="h-3.5 w-3.5 mr-1" /> Skicka till skolan
              </Button>
            )}
          </div>

          {reportData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiBox value={reportData.total} label="Totalt händelser" icon={Activity} color="bg-slate-50 dark:bg-slate-900" />
                <KpiBox value={reportData.vape_count} label="Vape-larm" icon={AlertTriangle} color="bg-red-50 dark:bg-red-950/30" />
                <KpiBox value={reportData.suspect_count} label="Misstanke" icon={Activity} color="bg-amber-50 dark:bg-amber-950/30" />
                <KpiBox value={reportData.confirmed} label="Bekräftade" icon={CheckCircle} color="bg-emerald-50 dark:bg-emerald-950/30" />
              </div>

              {/* Trend */}
              <Card>
                <CardContent className="py-3 flex items-center gap-2">
                  {reportData.trend_pct > 0 ? <TrendingUp className="h-4 w-4 text-red-500" /> : reportData.trend_pct < 0 ? <TrendingDown className="h-4 w-4 text-emerald-500" /> : <Activity className="h-4 w-4 text-slate-400" />}
                  <span className="text-sm">
                    Jämfört med föregående period: <strong className={reportData.trend_pct > 0 ? 'text-red-600' : reportData.trend_pct < 0 ? 'text-emerald-600' : ''}>
                      {reportData.trend_pct > 0 ? '+' : ''}{reportData.trend_pct}%
                    </strong> ({reportData.prev_period_count} föregående)
                  </span>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Top Rooms */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Mest drabbade rum</CardTitle></CardHeader>
                  <CardContent>
                    {reportData.top_rooms?.length > 0 ? (
                      <div className="space-y-2">
                        {reportData.top_rooms.map(([room, count], i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-xs font-medium w-32 truncate">{room}</span>
                            <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                              <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.min(100, (count / Math.max(reportData.total, 1)) * 100)}%` }} />
                            </div>
                            <span className="text-xs font-bold w-8 text-right">{count}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-muted-foreground py-4 text-center">Inga händelser under perioden</p>}
                  </CardContent>
                </Card>

                {/* Severity */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Allvarlighetsgrad</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { label: 'Hög', key: 'high', color: 'bg-red-500' },
                        { label: 'Medel', key: 'medium', color: 'bg-amber-500' },
                        { label: 'Låg', key: 'low', color: 'bg-blue-500' },
                      ].map(({ label, key, color }) => (
                        <div key={key} className="flex items-center gap-2">
                          <div className={`h-3 w-3 rounded-full ${color}`} />
                          <span className="text-xs w-16">{label}</span>
                          <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                            <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, ((reportData.severity?.[key] || 0) / Math.max(reportData.total, 1)) * 100)}%` }} />
                          </div>
                          <span className="text-xs font-bold w-8 text-right">{reportData.severity?.[key] || 0}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Heatmap */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Aktivitetsmönster (veckodag x timme)</CardTitle></CardHeader>
                <CardContent><Heatmap data={reportData.heatmap} /></CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===== STUDENT HEALTH ===== */}
        <TabsContent value="health" className="space-y-4 mt-4">
          <div className="flex items-center gap-3">
            <Select value={String(healthMonths)} onValueChange={v => setHealthMonths(parseInt(v))}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 månader</SelectItem>
                <SelectItem value="6">6 månader</SelectItem>
                <SelectItem value="12">12 månader</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadHealth} disabled={loading}>
              <Activity className="h-3.5 w-3.5 mr-1" /> Uppdatera
            </Button>
          </div>

          {healthData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiBox value={healthData.total_incidents} label="Totalt incidenter" icon={Activity} color="bg-slate-50 dark:bg-slate-900" />
                <KpiBox value={healthData.vape_count} label="Bekräftad vape" icon={AlertTriangle} color="bg-red-50 dark:bg-red-950/30" />
                <KpiBox value={healthData.confirmed} label="Personalbekräftade" icon={CheckCircle} color="bg-emerald-50 dark:bg-emerald-950/30" />
                <KpiBox value={`${healthData.trend_pct > 0 ? '+' : ''}${healthData.trend_pct}%`} label="Trend vs föregående" icon={healthData.trend_pct > 0 ? TrendingUp : TrendingDown} color={healthData.trend_pct > 0 ? 'bg-red-50 dark:bg-red-950/30' : 'bg-emerald-50 dark:bg-emerald-950/30'} />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Mest drabbade rum</CardTitle></CardHeader>
                  <CardContent>
                    {healthData.top_rooms?.length > 0 ? (
                      <div className="space-y-2">
                        {healthData.top_rooms.map(([room, count], i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-xs font-medium w-32 truncate">{room}</span>
                            <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                              <div className="h-full rounded-full bg-teal-500" style={{ width: `${Math.min(100, (count / Math.max(healthData.total_incidents, 1)) * 100)}%` }} />
                            </div>
                            <span className="text-xs font-bold w-8 text-right">{count}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-muted-foreground py-4 text-center">Inga händelser under perioden</p>}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Månadsvis trend</CardTitle></CardHeader>
                  <CardContent>
                    {healthData.monthly_trend && Object.keys(healthData.monthly_trend).length > 0 ? (
                      <div className="space-y-1.5">
                        {Object.entries(healthData.monthly_trend).sort().map(([key, count]) => {
                          const maxTrend = Math.max(...Object.values(healthData.monthly_trend), 1);
                          return (
                            <div key={key} className="flex items-center gap-2">
                              <span className="text-[10px] font-mono w-16 text-muted-foreground">{key}</span>
                              <div className="flex-1 h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                <div className="h-full rounded-full bg-gradient-to-r from-teal-400 to-teal-600" style={{ width: `${(count / maxTrend) * 100}%` }} />
                              </div>
                              <span className="text-xs font-bold w-6 text-right">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : <p className="text-xs text-muted-foreground py-4 text-center">Ingen data</p>}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Aktivitetsmönster</CardTitle></CardHeader>
                <CardContent><Heatmap data={healthData.heatmap} /></CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ===== PARENT LETTER ===== */}
        <TabsContent value="parent" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><MailIcon className="h-4 w-4" /> Generera föräldrabrev</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Skapa ett anonymiserat brev till vårdnadshavare. Ingen persondata inkluderas — bara aggregerad statistik för vald period.
              </p>
              {(!selectedSchool || selectedSchool === 'all') && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-300">Välj en skola i menyn ovan för att generera föräldrabrev.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Startdatum</label>
                  <Input type="date" value={letterStart} onChange={e => setLetterStart(e.target.value)} data-testid="letter-start-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Slutdatum</label>
                  <Input type="date" value={letterEnd} onChange={e => setLetterEnd(e.target.value)} data-testid="letter-end-date" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Skolans egna tillägg (valfritt)</label>
                <Textarea
                  value={letterText}
                  onChange={e => setLetterText(e.target.value)}
                  placeholder="T.ex. information om förebyggande insatser, kontaktuppgifter till elevhälsa..."
                  rows={3}
                  data-testid="letter-custom-text"
                />
              </div>
              <Button onClick={downloadParentLetter} disabled={!selectedSchool || selectedSchool === 'all'} className="bg-gradient-to-r from-emerald-600 to-teal-600" data-testid="generate-letter-btn">
                <Download className="h-4 w-4 mr-1.5" /> Generera föräldrabrev (PDF)
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
